import React, { useState, useMemo, useEffect } from 'react'
import { Container, Stepper, Step, StepLabel, Box, useTheme } from '@material-ui/core'
import { motion, AnimatePresence } from 'framer-motion'
import { Typography } from '@mui/material'

import { StyledPaper, SectionHeader, StyledButton, buttonVariants } from './styles/StyledComponents'
import StepSelectType from './components/StepSelectType'
import StepSelectFile from './components/StepSelectFile'
import BedPanelSelect from './components/BedPanelSelect'
import StepConfigure, { GENESUS_AUTOMATIC_COHORT } from './components/StepConfigure'
import StepClinicalData from './components/StepClinicalData'
import StepReview from './components/StepReview'
import { startGatkCohortAnalysis, startExomeDepthCohortAnalysis } from '../../../api/analysis/cohort'
import { startMultisampleAnalysis } from '../../../api/analysis/annotate'
import SuccessScreen from './components/SuccessScreen'
import CarrierConfigSelect from './components/CarrierConfigSelect'
import StepMultisampleSelect from './components/StepMultisampleSelect'
import { alpha } from '@material-ui/core/styles'
import { EMPTY_SPECIMEN, buildSpecimenPayload } from '../lib/specimen'
import {
  EMPTY_LIBRARY,
  RNA_ANALYSIS_TYPE,
  RNA_REFERENCE,
  buildRnaIntakeFields,
  isRnaSelection,
  pruneSpecimenSource,
} from '../lib/rnaLibrary'

// Maximum samples allowed per cohort (matches backend validation)
const MAX_COHORT_SAMPLES = 50

// The wizard spans the full width of Home, so it draws its own frame.
const WIZARD_PAPER_SX = {
  height: 'auto',
  maxHeight: 'none',
  p: { xs: 2, md: 2.5 },
}

// The file-selection step caps its height so its table scrolls inside the wizard.
// 260px is what sits around it (navbar and top gap 88, frame padding 40, stepper
// and gap 44, Back/Next row 68), so Next stays on screen without scrolling the page.
// The cap sits on the step's own wrapper, so it stays while the step animates out
// (a cap on the container lifted at once and the page jumped to the full table).
const FILE_STEP_STYLE = {
  maxHeight: 'max(360px, calc(100vh - 260px))',
  overflowY: 'auto',
  minHeight: 0,
}

// Single-row stepper: icon and label side by side, about 24px tall. Below md only
// the active step keeps its label, since five labelled steps need about 740px; on a
// phone only the numbered icons remain.
const getCompactStepperSx = (theme) => ({
  p: '0 !important',
  '& .MuiStepLabel-root': { py: 0 },
  '& .MuiStepIcon-root': { fontSize: '1.375rem' },
  '& .MuiStepLabel-label': {
    ...theme.typography.body2,
    whiteSpace: 'nowrap',
  },
  '& .MuiStepConnector-root': { minWidth: theme.spacing(2) },
  [theme.breakpoints.down('md')]: {
    '& .MuiStepLabel-labelContainer': { display: 'none' },
    '& .MuiStep-root:has(.Mui-active) .MuiStepLabel-labelContainer': { display: 'block' },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiStep-root:has(.Mui-active) .MuiStepLabel-labelContainer': { display: 'none' },
    '& .MuiStepConnector-root': { minWidth: theme.spacing(1) },
  },
})

const AnnotationView = ({ files = [], bedFiles = [], onAnnotate }) => {
  const theme = useTheme()
  const [activeStep, setActiveStep] = useState(0)
  const [analysisType, setAnalysisType] = useState('')
  const [selectedFiles, setSelectedFiles] = useState({ primary: null, normal: null })
  const [currentFileSelection, setCurrentFileSelection] = useState({ primary: null, normal: null })
  const [referenceGenome, setReferenceGenome] = useState('None')
  // Tumour type for somatic runs, as a whole OncoTree node ({oncotree_code, name,
  // tissue, ...}) rather than a bare string, so the review step can show the name
  // while the payload sends the code. Required for every somatic path (roadmap
  // R1.19): the configure step does not advance without it, and the type can be
  // changed later from the somatic view.
  const [oncotree, setOncotree] = useState(null)
  // Specimen metadata and other analyses of the same patient (roadmap R1.2a).
  // Optional; links made here start unverified.
  const [specimen, setSpecimen] = useState(EMPTY_SPECIMEN)
  const [linkedAnalyses, setLinkedAnalyses] = useState([])
  // The library of a tumour RNA analysis (roadmap R4.1).
  const [library, setLibrary] = useState(EMPTY_LIBRARY)
  // "Same tumour block as" only names a linked analysis: removing that link
  // clears the choice, so the specimen fields are sent again.
  useEffect(() => {
    setLibrary((current) => pruneSpecimenSource(current, linkedAnalyses))
  }, [linkedAnalyses])
  const [sequencingType, setSequencingType] = useState('')
  const [captureKit, setCaptureKit] = useState('')
  const [panelName, setPanelName] = useState('')
  const [runAI, setRunAI] = useState(false)
  const [runCnv, setRunCnv] = useState(false)
  const [runSv, setRunSv] = useState(false)
  const [runMod, setRunMod] = useState(false)
  const [cnvMethod, setCnvMethod] = useState('genesus-cnv')
  const [selectedCohortId, setSelectedCohortId] = useState(GENESUS_AUTOMATIC_COHORT)
  const [gatkCohorts, setGatkCohorts] = useState([])
  const [defaultCohort, setDefaultCohort] = useState(null)
  const [exomedepthCohorts, setExomedepthCohorts] = useState([])
  const [hpos, setHpos] = useState({ hpos: [] })
  const [diseases, setDiseases] = useState([])
  const [clinicalHistory, setClinicalHistory] = useState('')
  const [userGuidance, setUserGuidance] = useState('')
  const [isConfigureStepValid, setIsConfigureStepValid] = useState(false) // Add validation state
  const [clinicalAnalysisType, setClinicalAnalysisType] = useState('')

  // Multiple (cohort) analysis state
  const [cohortSelection, setCohortSelection] = useState([]) // array of pair ids
  const [cohortName, setCohortName] = useState('')
  const [cohortRows, setCohortRows] = useState([]) // selected row details
  const [cohortBedPanel, setCohortBedPanel] = useState('') // confirmed bed panel name
  const [pendingCohortBedPanel, setPendingCohortBedPanel] = useState('') // pending selection before Next
  const [cohortCnvMethod, setCohortCnvMethod] = useState('genesus-cnv') // 'genesus-cnv' or 'exomedepth'

  // Multisample analysis state
  const [multisampleSamples, setMultisampleSamples] = useState([
    { fileId: '', relation: 'Proband', include: true, gt: 'any', selectedOption: null },
  ])

  // Carrier analysis state
  const [carrierConfig, setCarrierConfig] = useState(null)

  // Technology type for Germline: '' | 'short-read' | 'ont-long-read' | 'pacbio-long-read' | 'vcf'
  const [technologyType, setTechnologyType] = useState('')

  // Submission states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [submittedFile, setSubmittedFile] = useState(null)
  const [paymentInfo, setPaymentInfo] = useState({ newBalance: null, deductedAmount: null })
  const [insufficientInfo, setInsufficientInfo] = useState({ neededBalance: null })
  // The server's reason when a start was refused before anything was created or charged.
  const [refusedText, setRefusedText] = useState(null)

  // Helper to check if ONT Long-Read flow is active
  const isOntFlow = analysisType === 'Germline' && technologyType === 'ont-long-read'

  // Helper to check if PacBio HiFi Long-Read flow is active
  const isPacbioFlow = analysisType === 'Germline' && technologyType === 'pacbio-long-read'

  // For the VCF Analysis flow, look up the selected file so we can gate UI on
  // its declared variant_type (CNV/SV VCFs don't support AI analysis).
  const selectedVcfFile = useMemo(
    () => (technologyType === 'vcf' ? files.find((f) => f.vcf_id && f.vcf_id === selectedFiles.primary) : null),
    [files, selectedFiles.primary, technologyType],
  )
  const isCnvOrSvVcf = selectedVcfFile?.variant_type === 'cnv' || selectedVcfFile?.variant_type === 'sv'

  // AI never runs on a somatic analysis: vcf_pipeline_wrapper enqueues
  // ai_pipeline_v3 only inside `if not is_somatic:`. The wizard nonetheless
  // offered the option AND billed for it, so the user was charged for a pipeline
  // that provably could not execute. Gating here stops the charge at source.
  //
  // Covers both somatic entry points: a tumour FASTQ run (analysisType) and a
  // tumour VCF upload (which sends isSomatic rather than an analysisType).
  const isSomatic = analysisType === 'Somatic'

  const aiUnavailable = isCnvOrSvVcf || isSomatic
  const aiUnavailableReason = isSomatic
    ? 'AI prioritization is not available for somatic analyses: it ranks variants by inherited-disease phenotype match, which has no tumour meaning.'
    : isCnvOrSvVcf
    ? 'AI prioritization is not available for CNV or SV VCFs: only SNP/Indel analyses are supported.'
    : null

  // Force the standard (non-AI) clinical path whenever AI cannot run, so the
  // request never carries runAI=true and never accrues the AI charge.
  useEffect(() => {
    if (aiUnavailable) {
      if (runAI) setRunAI(false)
      if (clinicalAnalysisType === 'ai-powered') setClinicalAnalysisType('standard')
    }
  }, [aiUnavailable, runAI, clinicalAnalysisType])

  // Dynamic steps based on analysis type
  const getSteps = () => {
    if (analysisType === 'Carrier') {
      return ['Select Analysis', 'Select Configuration', 'Select Files', 'Review & Submit']
    }
    if (analysisType === 'Multiple') {
      return ['Select Analysis', 'Select Bed Panel', 'Select Files', 'Review & Submit']
    }
    if (analysisType === 'Multisample') {
      return ['Select Analysis', 'Select Samples', 'Review & Submit']
    }
    if (isOntFlow) {
      return ['Select Analysis', 'Select Files', 'Clinical Data', 'Review & Submit']
    }
    if (isPacbioFlow) {
      return ['Select Analysis', 'Select Files', 'Clinical Data', 'Review & Submit']
    }
    return ['Select Analysis', 'Select Files', 'Configure', 'Clinical Data', 'Review & Submit']
  }

  const steps = getSteps()

  // Reset function for starting a new analysis
  const handleStartNew = () => {
    setActiveStep(0)
    setAnalysisType('')
    setSelectedFiles({ primary: null, normal: null })
    setCurrentFileSelection({ primary: null, normal: null })
    setReferenceGenome('None')
    setSequencingType('')
    setCaptureKit('')
    setPanelName('')
    setRunAI(false)
    setRunCnv(false) // Reset CNV flag
    setRunSv(false) // Reset SV flag
    setRunMod(false) // Reset MOD flag
    setCnvMethod('genesus-cnv') // Reset CNV method
    setSelectedCohortId('') // Reset selected cohort
    setGatkCohorts([]) // Reset GATK cohorts
    setExomedepthCohorts([]) // Reset ExomeDepth cohorts
    setHpos({ hpos: [] })
    setDiseases([])
    setClinicalHistory('')
    setUserGuidance('')
    setIsConfigureStepValid(false) // Reset validation state
    setClinicalAnalysisType('') // Reset clinical analysis type (AI vs Standard)
    setTechnologyType('') // Reset technology type
    setSpecimen(EMPTY_SPECIMEN)
    setLinkedAnalyses([])
    setLibrary(EMPTY_LIBRARY)
    setCarrierConfig(null)
    setCohortSelection([])
    setCohortName('')
    setCohortRows([]) // Reset cohort rows
    setCohortBedPanel('') // Reset cohort bed panel
    setPendingCohortBedPanel('') // Reset pending cohort bed panel
    setCohortCnvMethod('genesus-cnv') // Reset cohort CNV method
    setDefaultCohort(null)
    setMultisampleSamples([{ fileId: '', relation: 'Proband', include: true, gt: 'any', selectedOption: null }])
    setIsSubmitting(false)
    setIsSuccess(false)
    setSubmittedFile(null)
    setPaymentInfo({ newBalance: null, deductedAmount: null })
    setInsufficientInfo({ neededBalance: null })
    setRefusedText(null)
  }

  // Handle carrier configuration selection
  const handleCarrierConfigSelect = (config) => {
    setCarrierConfig(config)
  }

  const handleNext = () => {
    if (analysisType === 'Carrier') {
      if (activeStep === 2) {
        // Confirm file selection when Next is clicked on file selection step (step 2 for carrier)
        setSelectedFiles(currentFileSelection)
      }
    } else if (analysisType === 'Multiple') {
      if (activeStep === 1) {
        // Confirm bed panel selection
        setCohortBedPanel(pendingCohortBedPanel)
      }
    } else {
      if (activeStep === 1) {
        // Confirm file selection when Next is clicked on file selection step
        setSelectedFiles(currentFileSelection)
        setIsConfigureStepValid(false) // Reset validation when entering configure step
      }
    }
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))
  }

  const handleBack = () => {
    // ONT flow: Clinical Data is at step 2, reset when leaving it
    if (isOntFlow && activeStep === 2) {
      setClinicalAnalysisType('')
      setRunAI(false)
    }
    // PacBio flow: Clinical Data is at step 2, reset when leaving it
    if (isPacbioFlow && activeStep === 2) {
      setClinicalAnalysisType('')
      setRunAI(false)
    }
    // Standard flow: Reset clinical analysis type when leaving the Clinical Data step (step 3)
    // so the user sees the selection screen fresh when they return
    if (activeStep === 3 && !isOntFlow && !isPacbioFlow && analysisType !== 'Carrier' && analysisType !== 'Multiple') {
      setClinicalAnalysisType('')
      setRunAI(false)
    }
    // When going back from step 1 (Select Files) to step 0, reset technology type
    if (activeStep === 1) {
      setTechnologyType('')
    }
    setActiveStep((prev) => Math.max(prev - 1, 0))
  }

  const handleAnalysisTypeSelect = (type) => {
    setAnalysisType(type)
    setTechnologyType('') // Reset technology type when analysis type changes
    setSelectedFiles({ primary: null, normal: null })
    setCurrentFileSelection({ primary: null, normal: null })
    setCohortSelection([])
    setCohortName('')
    // Reset configuration when analysis type changes
    setSequencingType('')
    setCaptureKit('')
    setPanelName('')
    setIsConfigureStepValid(false) // Reset validation state
    handleNext()
    setReferenceGenome('None')
  }

  const handleFileSelect = (selection) => {
    setSelectedFiles(selection)
    setIsConfigureStepValid(false) // Reset validation state when files change
    handleNext()
  }

  const handleConfigure = () => {
    handleNext()
  }

  const handleClinicalDataComplete = () => {
    handleNext()
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      let submissionResult = null
      if (analysisType === 'Carrier') {
        // Handle carrier analysis - special case
        submissionResult = await handleCarrierSubmit()
      } else if (analysisType === 'Multiple') {
        submissionResult = await handleCohortSubmit()
      } else if (analysisType === 'Multisample') {
        submissionResult = await handleMultisampleSubmit()
      } else if (isOntFlow) {
        // Handle ONT Long-Read Germline analysis
        submissionResult = await handleOntSubmit()
      } else if (isPacbioFlow) {
        // Handle PacBio HiFi Long-Read Germline analysis
        submissionResult = await handlePacbioSubmit()
      } else {
        // Handle other analysis types (Germline, Somatic)
        submissionResult = await handleStandardSubmit()
      }
      if (submissionResult?.refused) setRefusedText(submissionResult.error || null)

      // Small delay for smooth transition
      setTimeout(() => {
        setIsSubmitting(false)
        setIsSuccess(true)
      }, 1000)
    } catch (error) {
      // Handle error case
      console.error('Submit error:', error)
      setTimeout(() => {
        setIsSubmitting(false)
      }, 1000)
    }
  }

  const handleCohortSubmit = async () => {
    const trimmedName = cohortName.trim()
    const fileIds = cohortSelection

    // Find the bed file ID from the selected bed panel
    const selectedBedFile = bedFiles.find((file) => file.name === cohortBedPanel)
    if (!selectedBedFile) {
      throw new Error('Selected bed panel not found')
    }

    if (cohortCnvMethod === 'exomedepth') {
      const result = await startExomeDepthCohortAnalysis({
        name: trimmedName,
        fastq_file_pair_id_list: fileIds,
        ref_bed_file_id: selectedBedFile.id,
      })
      return result
    } else {
      const result = await startGatkCohortAnalysis({
        name: trimmedName,
        fastq_file_pair_id_list: fileIds,
        ref_bed_file_id: selectedBedFile.id,
      })
      return result
    }
  }

  const handleOntSubmit = async () => {
    // Get the primary ONT BAM file object
    const primaryFile = files.find((f) => f.ont_bam_id === selectedFiles.primary)
    setSubmittedFile(primaryFile)

    // Transform HPO data to legacy format
    const hpoIds = hpos?.hpos || []
    const hpoNames = hpos?.hpoNames || []

    // Transform diseases data to legacy format
    const diseaseNames = diseases?.map((d) => d.disease || d.name || d.label || d) || []
    const diseaseKeys = diseases?.map((d) => d.key || d.id) || []

    // ONT BAM simplified payload — no sequencing type, capture kit fields
    const annotationPayload = {
      runAI,
      runCnv,
      runSv,
      runMod,
      hpos: hpoIds,
      hpoNames: hpoNames,
      diseases: diseaseNames,
      diseaseKeys: diseaseKeys,
      clinicalHistory: clinicalHistory || '',
      userGuidance: userGuidance || '',
    }

    const result = await onAnnotate(primaryFile, annotationPayload)
    if (result?.error === 'Insufficient balance') {
      setInsufficientInfo({ neededBalance: result?.needed_balance })
      return result
    }
    if (result && !result.error) {
      const nb = result.new_balance ?? result.newBalance
      const da = result.deducted_amount ?? result.deductedAmount
      if (nb != null || da != null) {
        setPaymentInfo({ newBalance: nb ?? null, deductedAmount: da ?? null })
      }
      return result
    }
    return result
  }

  const handlePacbioSubmit = async () => {
    // Get the primary PacBio BAM file object
    const primaryFile = files.find((f) => f.pacbio_bam_id === selectedFiles.primary)
    setSubmittedFile(primaryFile)

    // Transform HPO data to legacy format
    const hpoIds = hpos?.hpos || []
    const hpoNames = hpos?.hpoNames || []

    // Transform diseases data to legacy format
    const diseaseNames = diseases?.map((d) => d.disease || d.name || d.label || d) || []
    const diseaseKeys = diseases?.map((d) => d.key || d.id) || []

    // PacBio BAM simplified payload — no sequencing type, capture kit fields
    const annotationPayload = {
      runAI,
      runCnv,
      runSv,
      runMod,
      annotation_type: 'PACBIO_BAM',
      hpos: hpoIds,
      hpoNames: hpoNames,
      diseases: diseaseNames,
      diseaseKeys: diseaseKeys,
      clinicalHistory: clinicalHistory || '',
      userGuidance: userGuidance || '',
    }

    const result = await onAnnotate(primaryFile, annotationPayload)
    if (result?.error === 'Insufficient balance') {
      setInsufficientInfo({ neededBalance: result?.needed_balance })
      return result
    }
    if (result && !result.error) {
      const nb = result.new_balance ?? result.newBalance
      const da = result.deducted_amount ?? result.deductedAmount
      if (nb != null || da != null) {
        setPaymentInfo({ newBalance: nb ?? null, deductedAmount: da ?? null })
      }
      return result
    }
    return result
  }

  const handleCarrierSubmit = async () => {
    // For carrier analysis, we need to send multiple samples
    const samplesData = []

    // Handle 2-sample case with maternal/paternal roles
    if (carrierConfig?.samples_required === 2) {
      // Add maternal sample (primary)
      if (selectedFiles.primary) {
        const maternalFile = files.find((f) => (f.vcf_id || f.fastq_pair_id) === selectedFiles.primary)
        if (maternalFile) {
          const fileType = maternalFile.vcf_id ? 'VCF' : 'FASTQ'
          const sampleData = {
            type: fileType,
            fileId: selectedFiles.primary,
            role: 'maternal', // Add role information
            annotation: {
              reference: referenceGenome,
              runAI: false,
              isSnp: true,
              isCnv: false,
              type: fileType === 'VCF' ? undefined : sequencingType === 'Whole Genome' ? 'WholeGenome' : 'Targeted',
              targetedType: fileType === 'VCF' || sequencingType === 'Whole Genome' ? '' : sequencingType,
              panel: fileType === 'VCF' || sequencingType === 'Whole Genome' ? '' : captureKit,
              analysisType: 'Carrier',
              runCnv: runCnv,
            },
          }
          samplesData.push(sampleData)
        }
      }

      // Add paternal sample (normal)
      if (selectedFiles.normal) {
        const paternalFile = files.find((f) => (f.vcf_id || f.fastq_pair_id) === selectedFiles.normal)
        if (paternalFile) {
          const fileType = paternalFile.vcf_id ? 'VCF' : 'FASTQ'
          const sampleData = {
            type: fileType,
            fileId: selectedFiles.normal,
            role: 'paternal', // Add role information
            annotation: {
              reference: referenceGenome,
              runAI: false,
              isSnp: true,
              isCnv: false,
              type: fileType === 'VCF' ? undefined : sequencingType === 'Whole Genome' ? 'WholeGenome' : 'Targeted',
              targetedType: fileType === 'VCF' || sequencingType === 'Whole Genome' ? '' : sequencingType,
              panel: fileType === 'VCF' || sequencingType === 'Whole Genome' ? '' : captureKit,
              analysisType: 'Carrier',
              runCnv: runCnv,
            },
          }
          samplesData.push(sampleData)
        }
      }
    } else {
      // Handle 1 or 3+ samples case
      const selectedFileIds = Object.values(selectedFiles).filter(Boolean)

      for (const fileId of selectedFileIds) {
        const file = files.find((f) => (f.vcf_id || f.fastq_pair_id) === fileId)
        if (!file) continue

        // Determine file type
        const fileType = file.vcf_id ? 'VCF' : 'FASTQ'

        // Create sample data for this file
        const sampleData = {
          type: fileType,
          fileId: fileId,
          annotation: {
            reference: referenceGenome,
            runAI: false, // AI is typically run on the final combined report, not individual files
            isSnp: true,
            isCnv: false,
            // FASTQ-specific fields
            type: fileType === 'VCF' ? undefined : sequencingType === 'Whole Genome' ? 'WholeGenome' : 'Targeted',
            targetedType: fileType === 'VCF' || sequencingType === 'Whole Genome' ? '' : sequencingType,
            panel: fileType === 'VCF' || sequencingType === 'Whole Genome' ? '' : captureKit,
            analysisType: 'Carrier',
            runCnv: runCnv,
          },
        }

        samplesData.push(sampleData)
      }
    }

    // Create carrier analysis payload
    const carrierPayload = {
      annotation_type: 'CARRIER',
      carrierConfigId: carrierConfig.id,
      annotation: samplesData,
    }

    // Call the backend with carrier-specific payload
    const result = await onAnnotate(null, carrierPayload)
    if (result?.error === 'Insufficient balance') {
      setInsufficientInfo({ neededBalance: result?.needed_balance })
      return result
    }
    if (result && !result.error) {
      const nb = result.new_balance ?? result.newBalance
      const da = result.deducted_amount ?? result.deductedAmount
      if (nb != null || da != null) {
        setPaymentInfo({ newBalance: nb ?? null, deductedAmount: da ?? null })
      }
    }
    return result
  }

  const handleMultisampleSubmit = async () => {
    const samples = multisampleSamples
      .filter((s) => s.fileId)
      .map((s) => ({
        fileId: s.fileId,
        relation: s.relation,
        include: s.include,
        gt: s.gt,
      }))

    const result = await startMultisampleAnalysis({
      samples,
      analysisName: `Multisample - ${new Date().toLocaleString()}`,
    })

    if (result?.error === 'Insufficient balance') {
      setInsufficientInfo({ neededBalance: result?.needed_balance })
      return result
    }
    return result
  }

  const handleStandardSubmit = async () => {
    // Get the primary file object
    const primaryFile = files.find((f) => (f.vcf_id || f.fastq_pair_id || f.ont_bam_id) === selectedFiles.primary)
    setSubmittedFile(primaryFile)

    // Determine file type
    const fileType = primaryFile?.vcf_id ? 'VCF' : 'FASTQ'

    // Transform HPO data to legacy format
    const hpoIds = hpos?.hpos || []
    const hpoNames = hpos?.hpoNames || []

    // Transform diseases data to legacy format
    const diseaseNames = diseases?.map((d) => d.disease || d.name || d.label || d) || []
    const diseaseIds = diseases?.map((d) => d.omim || d.id) || []
    const diseaseKeys = diseases?.map((d) => d.key || d.id) || []

    let allSelectedFiles = []
    const isDemultiplexed = selectedFiles.isDemultiplexed
    if (isDemultiplexed) {
      allSelectedFiles = [selectedFiles.primary]
      for (const [key, value] of Object.entries(selectedFiles)) {
        // if key kas sample prefix, add the value to allSelectedFiles
        if (key.startsWith('sample')) {
          allSelectedFiles.push(value)
        }
      }
    }

    // VCF files declare their variant type at upload (snp_indel | cnv | sv).
    // Derive the SNP/CNV/SV flags from that declaration; the backend re-reads
    // VcfFile.variant_type and is authoritative.
    const vcfVariantType = fileType === 'VCF' ? primaryFile?.variant_type || 'snp_indel' : 'snp_indel'

    // A tumour RNA analysis (roadmap R4.1): tumour-only, a library instead of a
    // sequencing type, and never Sarek (the backend routes TumorOnlyRNA apart).
    const isRna = analysisType === 'Somatic' && fileType === 'FASTQ' && isRnaSelection(selectedFiles)
    const specimenPayload =
      analysisType === 'Somatic'
        ? buildSpecimenPayload(specimen, { paired: selectedFiles.somaticAnalysisType === 'tumor-normal' })
        : undefined
    // Sharing a linked DNA analysis's tumour specimen sends no specimen block of
    // its own; a choice whose analysis is no longer linked is dropped.
    const rnaFields = isRna ? buildRnaIntakeFields({ library, linkedAnalyses, specimenPayload }) : null

    // Create annotation payload in the old format
    const annotationPayload = {
      // An RNA analysis has no reference choice: it aligns on the GRCh38 RNA bundle.
      reference: isRna ? RNA_REFERENCE : referenceGenome,
      runAI,
      hpos: hpoIds,
      hpoNames: hpoNames,
      diseases: diseaseNames,
      diseaseIds: diseaseIds,
      diseaseKeys: diseaseKeys,
      clinicalHistory: clinicalHistory || '',
      userGuidance: userGuidance || '',
      isSnp: vcfVariantType === 'snp_indel',
      isCnv: vcfVariantType === 'cnv',
      isSv: vcfVariantType === 'sv',
      // FASTQ-specific fields (undefined for VCF)
      type: fileType === 'VCF' || isRna ? undefined : sequencingType === 'Whole Genome' ? 'WholeGenome' : 'Targeted',
      targetedType: fileType === 'VCF' || isRna || sequencingType === 'Whole Genome' ? '' : sequencingType,
      panel: fileType === 'VCF' || isRna || sequencingType === 'Whole Genome' ? '' : captureKit,
      analysisType:
        fileType === 'VCF'
          ? undefined
          : isRna
          ? RNA_ANALYSIS_TYPE
          : analysisType === 'Somatic'
          ? selectedFiles.somaticAnalysisType === 'tumor-normal'
            ? 'TumorNormal'
            : 'TumorOnly'
          : 'Germline',
      library: rnaFields ? rnaFields.library : undefined,
      // A VCF upload never sends analysisType (that carries FASTQ/Sarek routing semantics),
      // so somatic intent was silently dropped. isSomatic preserves it: the backend tags the
      // analysis "Somatic", sets VcfFile.is_somatic, and the VCF pipeline keeps non-diploid
      // (VAF-based) genotypes intact instead of treating them as germline diploid calls.
      isSomatic: fileType === 'VCF' && analysisType === 'Somatic',
      // Sent for EVERY somatic path (VCF upload, FASTQ tumour-only, FASTQ
      // tumour-normal), because OncoKB evidence levels are per-indication
      // regardless of how the variants were called. Required: the backend
      // refuses a somatic analysis without a code it finds in OncoTree, and
      // stores the table's name rather than this one.
      oncotreeCode: analysisType === 'Somatic' ? oncotree?.oncotree_code || '' : '',
      oncotreeName: analysisType === 'Somatic' ? oncotree?.name || '' : '',
      normalSampleId:
        analysisType === 'Somatic' && selectedFiles.somaticAnalysisType === 'tumor-normal' ? selectedFiles.normal : '',
      // Specimen metadata (undefined when nothing was entered) and the other
      // analyses of the patient, somatic only. The backend validates both before
      // any charge and links every analysis of the matched normal's upload itself.
      specimen: rnaFields ? rnaFields.specimen : specimenPayload,
      linkedAnalysisIds: analysisType === 'Somatic' ? linkedAnalyses.map((a) => a.analysis_id) : undefined,
      runCnv: runCnv,
      // CNV configuration for FASTQ analyses
      cnv_type: runCnv
        ? cnvMethod === 'genesus-cnv'
          ? 'GENESUS-CNV'
          : cnvMethod === 'exomedepth'
          ? 'EXOMEDEPTH'
          : cnvMethod === 'ecole'
          ? 'ECOLE'
          : undefined
        : undefined,
      cohort_id:
        runCnv && (cnvMethod === 'genesus-cnv' || cnvMethod === 'exomedepth')
          ? selectedCohortId && selectedCohortId !== GENESUS_AUTOMATIC_COHORT
            ? selectedCohortId
            : undefined
          : undefined,
      isDemultiplexed: isDemultiplexed,
      allSelectedFiles: allSelectedFiles,
    }

    // Call onAnnotate with the file object and payload (old format)
    const result = await onAnnotate(primaryFile, annotationPayload)
    if (result?.error === 'Insufficient balance') {
      setInsufficientInfo({ neededBalance: result?.needed_balance })
      return result
    }
    if (result && !result.error) {
      const nb = result.new_balance ?? result.newBalance
      const da = result.deducted_amount ?? result.deductedAmount
      if (nb != null || da != null) {
        setPaymentInfo({ newBalance: nb ?? null, deductedAmount: da ?? null })
      }
      return result
    }
    return result
  }

  const isStepComplete = (step) => {
    if (analysisType === 'Carrier') {
      switch (step) {
        case 0:
          return !!analysisType && activeStep > 0
        case 1:
          return !!carrierConfig && activeStep > 1
        case 2:
          return !!selectedFiles.primary && activeStep > 2
        case 3:
          return activeStep > 3
        default:
          return false
      }
    }

    if (analysisType === 'Multiple') {
      switch (step) {
        case 0:
          return !!analysisType && activeStep > 0
        case 1:
          return !!cohortBedPanel && activeStep > 1
        case 2:
          return cohortSelection.length >= 2 && activeStep > 2
        case 3:
          return activeStep > 3
        default:
          return false
      }
    }

    if (analysisType === 'Multisample') {
      switch (step) {
        case 0:
          return !!analysisType && activeStep > 0
        case 1:
          return multisampleSamples.filter((s) => s.fileId && s.relation).length >= 2 && activeStep > 1
        case 2:
          return activeStep > 2
        default:
          return false
      }
    }

    // ONT Long-Read Germline: 4 steps (Select Analysis, Select Files, Clinical Data, Review & Submit)
    if (isOntFlow) {
      switch (step) {
        case 0:
          return !!analysisType && activeStep > 0
        case 1:
          return !!selectedFiles.primary && activeStep > 1
        case 2:
          return activeStep > 2 && (!runAI || (runAI && hpos?.hpos?.length > 0 && clinicalHistory))
        case 3:
          return activeStep > 3
        default:
          return false
      }
    }

    // PacBio HiFi Long-Read Germline: 4 steps (Select Analysis, Select Files, Clinical Data, Review & Submit)
    if (isPacbioFlow) {
      switch (step) {
        case 0:
          return !!analysisType && activeStep > 0
        case 1:
          return !!selectedFiles.primary && activeStep > 1
        case 2:
          return activeStep > 2 && (!runAI || (runAI && hpos?.hpos?.length > 0 && clinicalHistory))
        case 3:
          return activeStep > 3
        default:
          return false
      }
    }

    switch (step) {
      case 0:
        return !!analysisType && activeStep > 0
      case 1:
        if (analysisType === 'Germline') {
          return !!selectedFiles.primary && activeStep > 1
        } else if (analysisType === 'Somatic') {
          const somaticType = selectedFiles.somaticAnalysisType || currentFileSelection.somaticAnalysisType
          if (somaticType === 'tumor-only' || somaticType === 'tumor-rna') {
            return !!selectedFiles.primary && activeStep > 1
          } else if (somaticType === 'tumor-normal') {
            return !!(selectedFiles.primary && selectedFiles.normal) && activeStep > 1
          } else if (somaticType === 'vcf') {
            return !!selectedFiles.primary && activeStep > 1
          }
        }
        return false
      case 2:
        if (analysisType === 'Somatic') {
          const somaticType = selectedFiles.somaticAnalysisType || currentFileSelection.somaticAnalysisType
          if (somaticType === 'vcf') {
            return isConfigureStepValid && referenceGenome !== 'None' && activeStep > 2
          }
        }
        return isConfigureStepValid && activeStep > 2
      case 3:
        return activeStep > 3 && (!runAI || (runAI && hpos?.hpos?.length > 0 && clinicalHistory))
      case 4:
        return activeStep > 4
      default:
        return false
    }
  }

  const canProceed = () => {
    if (analysisType === 'Carrier') {
      switch (activeStep) {
        case 0:
          return !!analysisType
        case 1:
          return !!carrierConfig
        case 2:
          // For carrier analysis, need sufficient file selection
          const requiredSamples = carrierConfig?.samples_required || 1
          const selectedCount = Object.values(currentFileSelection).filter(Boolean).length
          return selectedCount >= requiredSamples
        case 3:
          return true
        default:
          return false
      }
    }

    if (analysisType === 'Multiple') {
      switch (activeStep) {
        case 0:
          return !!analysisType
        case 1:
          return !!pendingCohortBedPanel
        case 2:
          return cohortSelection.length >= 1 && cohortSelection.length <= MAX_COHORT_SAMPLES // TODO: Revert this to 2 after testing
        case 3:
          return true
        default:
          return false
      }
    }

    if (analysisType === 'Multisample') {
      switch (activeStep) {
        case 0:
          return !!analysisType
        case 1:
          // Need at least 2 valid samples (with fileId and relation)
          return multisampleSamples.filter((s) => s.fileId && s.relation).length >= 2
        case 2:
          return true // Review step
        default:
          return false
      }
    }

    // ONT Long-Read Germline: 4 steps
    if (isOntFlow) {
      switch (activeStep) {
        case 0:
          return !!analysisType
        case 1:
          // Require technology type selected AND a file selected
          return !!technologyType && !!currentFileSelection.primary
        case 2:
          // Clinical Data step — require clinicalAnalysisType selected
          return !!clinicalAnalysisType && (!runAI || (runAI && hpos?.hpos?.length > 0 && clinicalHistory))
        case 3:
          return true // Review step
        default:
          return false
      }
    }

    // PacBio HiFi Long-Read Germline: 4 steps
    if (isPacbioFlow) {
      switch (activeStep) {
        case 0:
          return !!analysisType
        case 1:
          // Require technology type selected AND a file selected
          return !!technologyType && !!currentFileSelection.primary
        case 2:
          // Clinical Data step — require clinicalAnalysisType selected
          return !!clinicalAnalysisType && (!runAI || (runAI && hpos?.hpos?.length > 0 && clinicalHistory))
        case 3:
          return true // Review step
        default:
          return false
      }
    }

    switch (activeStep) {
      case 0:
        return !!analysisType
      case 1:
        if (analysisType === 'Germline') {
          // Also require technologyType for Germline (short-read must be selected)
          if (!technologyType) return false
          if (currentFileSelection.isDemultiplexed) {
            const { isDemultiplexed, somaticAnalysisType, ...files } = currentFileSelection
            const selectedCount = Object.values(files).filter(Boolean).length
            return selectedCount >= 2
          }
          return !!currentFileSelection.primary
        } else if (analysisType === 'Somatic') {
          const somaticType = currentFileSelection.somaticAnalysisType
          // First check if somatic analysis type is selected
          if (!somaticType) return false

          if (somaticType === 'tumor-only' || somaticType === 'tumor-rna') {
            return !!currentFileSelection.primary
          } else if (somaticType === 'tumor-normal') {
            return !!(currentFileSelection.primary && currentFileSelection.normal)
          } else if (somaticType === 'vcf') {
            return !!currentFileSelection.primary
          }
        }
        return false
      case 2:
        if (analysisType === 'Germline') {
          if (sequencingType) {
            return isConfigureStepValid
          }
          return isConfigureStepValid && referenceGenome !== 'None'
        } else if (analysisType === 'Somatic') {
          const somaticType = selectedFiles.somaticAnalysisType || currentFileSelection.somaticAnalysisType
          if (somaticType === 'vcf') {
            return isConfigureStepValid && referenceGenome !== 'None'
          }
        }
        return isConfigureStepValid
      case 3:
        // Require clinicalAnalysisType to be selected before allowing next
        return !!clinicalAnalysisType && (!runAI || (runAI && hpos?.hpos?.length > 0 && clinicalHistory))
      case 4:
        return true
      default:
        return false
    }
  }

  // Simple, clear transitions
  const pageTransition = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2, ease: 'easeInOut' },
  }

  const renderStepContent = () => {
    const stepProps = {
      key: activeStep,
      ...pageTransition,
    }
    const fileStepProps = { ...stepProps, style: FILE_STEP_STYLE }

    if (analysisType === 'Carrier') {
      switch (activeStep) {
        case 0:
          return (
            <motion.div {...stepProps}>
              <StepSelectType onAnalysisTypeSelect={handleAnalysisTypeSelect} />
            </motion.div>
          )
        case 1:
          return (
            <motion.div {...stepProps}>
              <CarrierConfigSelect selectedConfigId={carrierConfig?.id} onConfigSelect={handleCarrierConfigSelect} />
            </motion.div>
          )
        case 2:
          return (
            <motion.div {...fileStepProps}>
              <StepSelectFile
                files={files}
                analysisType={analysisType}
                selectedFiles={selectedFiles}
                onFileSelect={handleFileSelect}
                onSelectionChange={setCurrentFileSelection}
                carrierConfig={carrierConfig}
              />
            </motion.div>
          )
        case 3:
          return (
            <motion.div {...stepProps}>
              <StepReview
                analysisType={analysisType}
                selectedFiles={selectedFiles}
                files={files}
                carrierConfig={carrierConfig}
                bedFiles={bedFiles}
                onSubmit={handleSubmit}
                onDiscard={handleStartNew}
                isSubmitting={isSubmitting}
                handleBack={handleBack}
              />
            </motion.div>
          )
        default:
          return null
      }
    }

    // ONT Long-Read Germline: 4 steps
    if (isOntFlow) {
      switch (activeStep) {
        case 0:
          return (
            <motion.div {...stepProps}>
              <StepSelectType onAnalysisTypeSelect={handleAnalysisTypeSelect} />
            </motion.div>
          )
        case 1:
          return (
            <motion.div {...fileStepProps}>
              <StepSelectFile
                files={files}
                analysisType={analysisType}
                selectedFiles={selectedFiles}
                onFileSelect={handleFileSelect}
                onSelectionChange={setCurrentFileSelection}
                technologyType={technologyType}
                onTechnologyTypeSelect={setTechnologyType}
              />
            </motion.div>
          )
        case 2:
          return (
            <motion.div {...stepProps}>
              <StepClinicalData
                runAI={runAI}
                hpos={hpos}
                diseases={diseases}
                clinicalHistory={clinicalHistory}
                userGuidance={userGuidance}
                onRunAIChange={setRunAI}
                onHposChange={setHpos}
                onDiseasesChange={setDiseases}
                onClinicalHistoryChange={setClinicalHistory}
                onUserGuidanceChange={setUserGuidance}
                clinicalAnalysisType={clinicalAnalysisType}
                setClinicalAnalysisType={setClinicalAnalysisType}
                isOntFlow={true}
                runCnv={runCnv}
                onRunCnvChange={setRunCnv}
                runSv={runSv}
                onRunSvChange={setRunSv}
                runMod={runMod}
                onRunModChange={setRunMod}
              />
            </motion.div>
          )
        case 3:
          return (
            <motion.div {...stepProps}>
              <StepReview
                analysisType={analysisType}
                selectedFiles={selectedFiles}
                files={files}
                runAI={runAI}
                runCnv={runCnv}
                runSv={runSv}
                runMod={runMod}
                hpos={hpos}
                diseases={diseases}
                clinicalHistory={clinicalHistory}
                userGuidance={userGuidance}
                bedFiles={bedFiles}
                onSubmit={handleSubmit}
                onDiscard={handleStartNew}
                isSubmitting={isSubmitting}
                handleBack={handleBack}
                technologyType={technologyType}
              />
            </motion.div>
          )
        default:
          return null
      }
    }

    // PacBio HiFi Long-Read Germline: 4 steps
    if (isPacbioFlow) {
      switch (activeStep) {
        case 0:
          return (
            <motion.div {...stepProps}>
              <StepSelectType onAnalysisTypeSelect={handleAnalysisTypeSelect} />
            </motion.div>
          )
        case 1:
          return (
            <motion.div {...fileStepProps}>
              <StepSelectFile
                files={files}
                analysisType={analysisType}
                selectedFiles={selectedFiles}
                onFileSelect={handleFileSelect}
                onSelectionChange={setCurrentFileSelection}
                technologyType={technologyType}
                onTechnologyTypeSelect={setTechnologyType}
              />
            </motion.div>
          )
        case 2:
          return (
            <motion.div {...stepProps}>
              <StepClinicalData
                runAI={runAI}
                hpos={hpos}
                diseases={diseases}
                clinicalHistory={clinicalHistory}
                userGuidance={userGuidance}
                onRunAIChange={setRunAI}
                onHposChange={setHpos}
                onDiseasesChange={setDiseases}
                onClinicalHistoryChange={setClinicalHistory}
                onUserGuidanceChange={setUserGuidance}
                clinicalAnalysisType={clinicalAnalysisType}
                setClinicalAnalysisType={setClinicalAnalysisType}
                isPacbioFlow={true}
                runCnv={runCnv}
                onRunCnvChange={setRunCnv}
                runSv={runSv}
                onRunSvChange={setRunSv}
                runMod={runMod}
                onRunModChange={setRunMod}
                methylationAvailable={
                  files.find((f) => f.pacbio_bam_id === selectedFiles.primary)?.methylation_available
                }
              />
            </motion.div>
          )
        case 3:
          return (
            <motion.div {...stepProps}>
              <StepReview
                analysisType={analysisType}
                selectedFiles={selectedFiles}
                files={files}
                runAI={runAI}
                runCnv={runCnv}
                runSv={runSv}
                runMod={runMod}
                hpos={hpos}
                diseases={diseases}
                clinicalHistory={clinicalHistory}
                userGuidance={userGuidance}
                bedFiles={bedFiles}
                onSubmit={handleSubmit}
                onDiscard={handleStartNew}
                isSubmitting={isSubmitting}
                handleBack={handleBack}
                technologyType={technologyType}
              />
            </motion.div>
          )
        default:
          return null
      }
    }

    // Multisample flow: 3 steps
    if (analysisType === 'Multisample') {
      switch (activeStep) {
        case 0:
          return (
            <motion.div {...stepProps}>
              <StepSelectType onAnalysisTypeSelect={handleAnalysisTypeSelect} />
            </motion.div>
          )
        case 1:
          return (
            <motion.div {...stepProps}>
              <StepMultisampleSelect samples={multisampleSamples} onChange={setMultisampleSamples} />
            </motion.div>
          )
        case 2:
          return (
            <motion.div {...stepProps}>
              <StepReview
                analysisType={analysisType}
                files={files}
                multisampleSamples={multisampleSamples}
                bedFiles={bedFiles}
                onSubmit={handleSubmit}
                onDiscard={handleStartNew}
                isSubmitting={isSubmitting}
                handleBack={handleBack}
              />
            </motion.div>
          )
        default:
          return null
      }
    }

    switch (activeStep) {
      case 0:
        return (
          <motion.div {...stepProps}>
            <StepSelectType onAnalysisTypeSelect={handleAnalysisTypeSelect} />
          </motion.div>
        )
      case 1:
        if (analysisType === 'Multiple') {
          return (
            <motion.div {...stepProps}>
              <BedPanelSelect
                defaultSelectedName={pendingCohortBedPanel || cohortBedPanel}
                onSelectionChange={setPendingCohortBedPanel}
              />
            </motion.div>
          )
        }
        return (
          <motion.div {...fileStepProps}>
            <StepSelectFile
              files={files}
              analysisType={analysisType}
              selectedFiles={selectedFiles}
              onFileSelect={handleFileSelect}
              onSelectionChange={setCurrentFileSelection}
              technologyType={technologyType}
              onTechnologyTypeSelect={setTechnologyType}
            />
          </motion.div>
        )
      case 2:
        if (analysisType === 'Multiple') {
          return (
            <motion.div {...fileStepProps}>
              <StepSelectFile
                files={files}
                analysisType={analysisType}
                selectedFiles={selectedFiles}
                onFileSelect={handleFileSelect}
                onSelectionChange={setCurrentFileSelection}
                cohortSelection={cohortSelection}
                onCohortSelectionChange={setCohortSelection}
                onCohortSelectedRowsChange={setCohortRows}
                onCohortBedPanelChange={setCohortBedPanel}
                selectedCohortBedPanel={cohortBedPanel}
              />
            </motion.div>
          )
        }
        return (
          <motion.div {...stepProps}>
            <StepConfigure
              analysisType={analysisType}
              selectedFiles={selectedFiles}
              files={files}
              referenceGenome={referenceGenome}
              onReferenceGenomeChange={setReferenceGenome}
              sequencingType={sequencingType}
              onSequencingTypeChange={setSequencingType}
              captureKit={captureKit}
              onCaptureKitChange={setCaptureKit}
              panelName={panelName}
              onPanelNameChange={setPanelName}
              bedFiles={bedFiles}
              onNext={handleConfigure}
              onValidationChange={setIsConfigureStepValid}
              runCnv={runCnv}
              onRunCnvChange={setRunCnv}
              cnvMethod={cnvMethod}
              onCnvMethodChange={setCnvMethod}
              selectedCohortId={selectedCohortId}
              onSelectedCohortIdChange={setSelectedCohortId}
              onGatkCohortsChange={setGatkCohorts}
              onDefaultCohortChange={setDefaultCohort}
              onExomedepthCohortsChange={setExomedepthCohorts}
              oncotree={oncotree}
              onOncotreeChange={setOncotree}
              specimen={specimen}
              onSpecimenChange={setSpecimen}
              linkedAnalyses={linkedAnalyses}
              onLinkedAnalysesChange={setLinkedAnalyses}
              library={library}
              onLibraryChange={setLibrary}
            />
          </motion.div>
        )
      case 3:
        if (analysisType === 'Multiple') {
          return (
            <motion.div {...stepProps}>
              <StepReview
                analysisType={analysisType}
                files={files}
                cohortName={cohortName}
                onCohortNameChange={setCohortName}
                cohortSelection={cohortSelection}
                cohortRows={cohortRows}
                cohortBedPanel={cohortBedPanel}
                cohortCnvMethod={cohortCnvMethod}
                onCohortCnvMethodChange={setCohortCnvMethod}
                bedFiles={bedFiles}
                onSubmit={handleSubmit}
                onDiscard={handleStartNew}
                isSubmitting={isSubmitting}
                handleBack={handleBack}
              />
            </motion.div>
          )
        }
        return (
          <motion.div {...stepProps}>
            <StepClinicalData
              runAI={runAI}
              hpos={hpos}
              diseases={diseases}
              clinicalHistory={clinicalHistory}
              userGuidance={userGuidance}
              onRunAIChange={setRunAI}
              onHposChange={setHpos}
              onDiseasesChange={setDiseases}
              onClinicalHistoryChange={setClinicalHistory}
              onUserGuidanceChange={setUserGuidance}
              clinicalAnalysisType={clinicalAnalysisType}
              setClinicalAnalysisType={setClinicalAnalysisType}
              aiDisabled={aiUnavailable}
              aiDisabledReason={aiUnavailableReason}
            />
          </motion.div>
        )
      case 4:
        return (
          <motion.div {...stepProps}>
            <StepReview
              analysisType={analysisType}
              selectedFiles={selectedFiles}
              files={files}
              referenceGenome={referenceGenome}
              sequencingType={sequencingType}
              captureKit={captureKit}
              panelName={panelName}
              runAI={runAI}
              hpos={hpos}
              diseases={diseases}
              clinicalHistory={clinicalHistory}
              userGuidance={userGuidance}
              runCnv={runCnv}
              cnvMethod={cnvMethod}
              selectedCohortId={selectedCohortId}
              gatkCohorts={gatkCohorts}
              defaultCohort={defaultCohort}
              exomedepthCohorts={exomedepthCohorts}
              bedFiles={bedFiles}
              onSubmit={handleSubmit}
              onDiscard={handleStartNew}
              isSubmitting={isSubmitting}
              handleBack={handleBack}
              specimen={specimen}
              linkedAnalyses={linkedAnalyses}
              library={library}
            />
          </motion.div>
        )
      default:
        return null
    }
  }

  // Don't render stepper, buttons, or progress bar when showing success
  if (isSuccess) {
    return (
      <Container maxWidth={false} disableGutters sx={{ display: 'flex', flexDirection: 'column' }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <StyledPaper sx={WIZARD_PAPER_SX}>
            <Box
              sx={{
                my: 1,
                flex: 1, // Use flex: 1 instead of fixed height
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 0, // Allow container to shrink
                overflow: 'auto', // Allow scrolling if needed
              }}
            >
              <SuccessScreen
                onStartNew={handleStartNew}
                submittedFile={submittedFile}
                analysisType={analysisType}
                newBalance={paymentInfo.newBalance}
                deductedAmount={paymentInfo.deductedAmount}
                insufficientNeeded={insufficientInfo.neededBalance}
                notStartedText={refusedText}
              />
            </Box>
          </StyledPaper>
        </motion.div>
      </Container>
    )
  }

  return (
    <>
      <Container maxWidth={false} disableGutters sx={{ display: 'flex', flexDirection: 'column' }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <StyledPaper sx={WIZARD_PAPER_SX}>
            <SectionHeader>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: 'easeInOut', delay: 0.1 }}
              >
                <Stepper activeStep={activeStep} sx={getCompactStepperSx(theme)}>
                  {steps.map((label, index) => (
                    <Step key={label} completed={isStepComplete(index)}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </motion.div>
            </SectionHeader>

            <Box
              sx={{
                mt: 2.5,
                flex: 1,
                width: '100%',
                overflow: 'auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* framer-motion 4 ignores mode="wait"; exitBeforeEnter is its spelling. Without
                  it the old and new step render stacked for a moment and, now the wizard
                  has no fixed cap, the whole page jumps on every step change. */}
              <AnimatePresence exitBeforeEnter>{renderStepContent()}</AnimatePresence>
            </Box>

            {activeStep > 0 && activeStep < steps.length - 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: 'easeInOut', delay: 0.15 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mt: 2.5 }}>
                  <StyledButton variant="outlined" onClick={handleBack}>
                    Back
                  </StyledButton>

                  {/* File Selection Summary - Show for Carrier Analysis */}
                  {analysisType === 'Carrier' &&
                    carrierConfig &&
                    activeStep === 2 &&
                    Object.values(currentFileSelection).filter(Boolean).length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          px: 3,
                          py: 1.5,
                          backgroundColor: alpha(theme.palette.success.dark, 0.08),
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.success.dark, 0.19)}`,
                          flex: 1,
                          maxWidth: 600,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.success.dark,
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            flexShrink: 0,
                          }}
                        >
                          Selected:
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: 1.5,
                            alignItems: 'stretch',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {carrierConfig?.samples_required === 2 ? (
                            // For 2 samples: show Maternal/Paternal
                            <>
                              {currentFileSelection.primary && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 0.25,
                                    px: 1.5,
                                    py: 0.5,
                                    backgroundColor: theme.palette.success.dark,
                                    color: 'white',
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    width: '100%',
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                      M:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.75rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flexGrow: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      {files.find((f) => (f.vcf_id || f.fastq_pair_id) === currentFileSelection.primary)
                                        ?.sample || 'Unknown'}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontStyle: 'italic',
                                      color: 'text.secondary',
                                      textAlign: 'left',
                                      width: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {(() => {
                                      const file = files.find(
                                        (f) => (f.vcf_id || f.fastq_pair_id) === currentFileSelection.primary,
                                      )
                                      if (file?.file_type === 'vcf') {
                                        return file?.vcf_filename || 'VCF File'
                                      } else if (file?.file_type === 'fastq') {
                                        return file?.fastq1_filename || 'FASTQ File'
                                      } else {
                                        return 'Unknown file'
                                      }
                                    })()}
                                  </Typography>
                                </Box>
                              )}
                              {currentFileSelection.normal && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 0.25,
                                    px: 1.5,
                                    py: 0.5,
                                    backgroundColor: theme.palette.success.dark,
                                    color: 'white',
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    width: '100%',
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>
                                      P:
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.75rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flexGrow: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      {files.find((f) => (f.vcf_id || f.fastq_pair_id) === currentFileSelection.normal)
                                        ?.sample || 'Unknown'}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontStyle: 'italic',
                                      color: 'text.secondary',
                                      textAlign: 'left',
                                      width: '100%',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {(() => {
                                      const file = files.find(
                                        (f) => (f.vcf_id || f.fastq_pair_id) === currentFileSelection.normal,
                                      )
                                      if (file?.file_type === 'vcf') {
                                        return file?.vcf_filename || 'VCF File'
                                      } else if (file?.file_type === 'fastq') {
                                        return file?.fastq1_filename || 'FASTQ File'
                                      } else {
                                        return 'Unknown file'
                                      }
                                    })()}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : (
                            // For 1 or more than 2 samples: show all selected files
                            Object.entries(currentFileSelection)
                              .filter(([key, value]) => value)
                              .map(([key, fileId], index) => {
                                const file = files.find((f) => (f.vcf_id || f.fastq_pair_id) === fileId)
                                if (!file) return null

                                return (
                                  <Box
                                    key={key}
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: 0.25,
                                      px: 1.5,
                                      py: 0.5,
                                      backgroundColor: theme.palette.success.dark,
                                      color: 'white',
                                      borderRadius: 1,
                                      fontSize: '0.75rem',
                                      width: '100%',
                                      minWidth: 0,
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      {index + 1}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.75rem',
                                        textAlign: 'center',
                                        width: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {file.sample || 'Unknown'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.75rem',
                                        fontStyle: 'italic',
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                        width: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {file.name || 'Unknown file'}
                                    </Typography>
                                  </Box>
                                )
                              })
                          )}
                        </Box>
                      </Box>
                    )}

                  <StyledButton variant="contained" onClick={handleNext} disabled={!canProceed()}>
                    Next
                  </StyledButton>
                </Box>
              </motion.div>
            )}

            {/* Removed bottom progress bar for more space */}
          </StyledPaper>
        </motion.div>
      </Container>
    </>
  )
}

export default AnnotationView
